<template>
  <div class="highlight__container">

    <div v-if="!firstLoad" class="highlight__info">

      <div class="buttons mb-3">
        <button 
          v-if="can.markAsCompleted && !drawingHighlight.is_completed && editable"
          @click="confirmMarkComplete()"
          :disabled="drawingHighlight.files.length == 0"
          class="button is-link is-fullwidth">
          <span>Mark as Complete</span>
        </button>

        <template
          v-if="can.markAsApproved && drawingHighlight.is_completed && !drawingHighlight.is_approved">
          <button 
            @click="confirmRevision()"
            class="button is-warning is-fullwidth">
            <span>Request Revision</span>
          </button>

          <button 
            @click="confirmMarkApproved()"
            class="button is-link is-fullwidth">
            <span>Mark as Approved</span>
          </button>
        </template>
        
        <button 
          @click="addComment()"
          class="button is-fullwidth">
          <span class="icon">
            <i class="far fa-comment"></i>
          </span>
          <span>Leave a Comment</span>
        </button>
      </div>

      <div class="field">
        <label class="label">Status</label>
        <div class="is-capitalized">
          {{ drawingHighlight.status.toLowerCase() }}
        </div>
      </div>

      <div class="field">
        <label class="label">Requested On</label>
        <div>
          {{ formatDate(parseDate(drawingHighlight.created_on)) }}
        </div>
      </div>

      <div class="field" v-if="drawingHighlight.is_completed">
        <label class="label">Completed On</label>
        <div>
          {{ formatDate(parseDate(drawingHighlight.completed_on)) }}
        </div>
      </div>

      <div class="field" v-if="drawingHighlight.is_in_revision">
        <label class="label">Revision Requested On</label>
        <div>
          {{ formatDate(parseDate(drawingHighlight.in_revision_on)) }}
        </div>
      </div>

      <div class="field" v-if="drawingHighlight.is_approved">
        <label class="label">Approved On</label>
        <div>
          {{ formatDate(parseDate(drawingHighlight.approved_on)) }}
        </div>
      </div>

      <div class="field">
        <label class="label">RFQ</label>
        <router-link 
          :to="{ 
            name: 'RfqViewer', 
            params: {rfqId: drawingHighlight.project_rfq_id} }">
          {{ drawingHighlight.project_rfq.rfq_name }}
        </router-link>
      </div>

      <div class="field">
        <label class="label">Drawings</label>
        <a 
          v-if="drawingHighlight.project_rfq.drawing"
          :href="drawingHighlight.project_rfq.drawing" 
          target="_blank">
          Architectural Drawings
        </a>
        <span class="has-text-grey" v-else>
          No Drawings Available
        </span>
      </div>

      <!-- <div class="column is-one-quarter">
        <label class="label">Drawings</label>
        <a :href="highlight.project_rfq.attachment" target="_blank">
          {{ highlight.project_rfq.rfq_name }}
        </a>
      </div> -->
    </div>

    <div v-if="!firstLoad" class="highlight__details">

      <v-alert 
        class="mb-5"
        v-if="!drawingHighlight.project_rfq.is_accepted && 
          !drawingHighlight.is_approved"
        type="danger">
        This drawing highlight cannot be started because the RFQ has not yet been
        approved by an estimator.
      </v-alert>

      <div class="level">
        <div class="title is-5 level-left mb-0">
          Drawing Highlight Files
        </div>
        <button
          v-if="editable"
          @click="addHighlight()" 
          class="button is-primary is-small is-rounded level-right">
          <span class="icon">
            <i class="fa fa-plus"></i>
          </span>
          <span>Add Drawing Highlight</span>
        </button>
      </div>

      <table class="table is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>
              Elevation Display Name
            </th>
            <th>
              Drawing Highlight File
            </th>
            <th>
              Uploaded On
            </th>
            <th v-if="editable">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="v-centered" 
            v-for="(file, i) in files"
            :key="i">
            <!-- <td>
              <v-select 
                required
                :options="elevations"
                item-text="elevation_name"
                item-value="id"
                :disabled="!!file.id && editingId != file.id"
                v-model="file.elevation_id"
                @input="file.id ? updateElevation(file.id, 'elevation_id', $event) : ''"/>
            </td> -->
            <td>
              <v-text-field
                required
                placeholder="Elevation display name"
                v-model="file.elevation"
                :disabled="(!!file.id && editingId != file.id) || !editable"
                @blur="file.id ? updateElevation(file.id, 'elevation', $event) : ''"/>
            </td>

            <td>
              <div class="flex flex--align-center">
                <v-file-upload
                  class="mr-3"
                  v-if="!file.id || editingId == file.id"
                  has-name
                  v-model="file.image"
                  :disabled="(!!file.id && editingId != file.id) || !editable"
                  @input="file.id ? updateFileImage(file, $event) : ''"/>
                <a 
                  v-if="file.id && file.image"
                  :href="file.image"
                  target="_blank">
                  <span v-if="file.filename">
                    {{ file.filename }}
                  </span>
                  <span v-else>
                    drawing highlight file link
                  </span>
                </a>
                <div
                  class="has-text-grey" 
                  v-if="file.id && !file.image">
                  No image for this highlight
                </div>
              </div>
            </td>

            <td>
              {{ formatDate(parseDate(file.created_on)) }}
            </td>

            <td v-if="editable">
              <div
                class="buttons buttons-right">
                <template v-if="!file.id || editingId != file.id">
                  <button 
                    v-if="!file.id"
                    :disabled="!file.elevation || !file.image.file || file.image.file == ''"
                    @click.once="saveHighlightFile(file, i)"
                    class="button is-small is-link">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                    <span>
                      Save
                    </span>
                  </button>

                  <button
                    v-if="file.id"
                    @click="editingId = file.id"
                    class="button is-small is-primary">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                    <span>
                      Edit
                    </span>
                  </button>

                  <button
                    @click="file.id ? deleteHighlightFile(file.id) : files.splice(i,1)"
                    class="button is-small has-text-grey">
                    <span class="icon">
                      <i class="fa fa-trash"></i>
                    </span>
                    <span>
                      Delete
                    </span>
                  </button>
                </template>

                <template v-if="file.id && editingId == file.id">
                  <button
                    @click="editingId = null"
                    class="button is-small has-text-grey">
                    <span class="icon">
                      <i class="fa fa-trash"></i>
                    </span>
                    <span>
                      Exit Editing
                    </span>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <hr>

      <div>
        <div class="title is-5 mb-0">Drawing Highlight SVG</div>
        <div class="help mb-3">Used as an interactive graphic as part of the proposal</div>

        <v-drop-zone
          :id="`drawing-highlight-${drawingHighlight.id}`"
          :value="drawingHighlight.drawing_svg"
          :disabled="!editable"
          @input="addMasterHighlightSvg"
          height="150px"
          :file-name.sync="drawingHighlight.drawing_svg_filename"/>

        <div class="help mb-3">
          Notes:
          <div>
            1) Please ensure that each item is a separate layer in the PSD file
          </div>
          <div>
            2) Please ensure that the name of each layer matches the architecture name of an RFQ item
          </div>
        </div>
      </div>
      
      <template v-if="drawingHighlight.history.length > 0">
        <hr>

        <div class="title is-5">History</div>

        <table class="table is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>Update Date</th>
              <th>Updated By</th>
              <th>Change</th>
              <th>Comment</th>
            </tr>
          </thead>

          <tbody>
            <tr 
              v-for="update in drawingHighlight.history"
              :key="update.id">
              <td>
                {{ formatDate(parseDate(update.created_on)) }}
              </td>
              <td>
                {{ update.created_user ? update.created_user.display_name : '' }}
              </td>
              <td>
                {{ update.change }}
              </td>
              <td>
                <span v-html="update.comments"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <v-confirm-modal 
      v-model="confirmDialog.state"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :note="confirmDialog.note"
      :type="confirmDialog.type"
      @confirm="confirmedCb ? confirmedCb() : null"
      @cancel="confirmDialog.state = false">
      <div>
        <label class="label">Leave a Comment</label>
        <v-rich-text class="has-text-left" v-model="comments"/>
      </div>
    </v-confirm-modal>
  
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
// import VSelect from '@/components/base/VSelect'
import VFileUpload from '@/components/base/VFileUpload'
import VDropZone from '@/components/base/VDropZone'
import VConfirmModal from '@/components/base/VConfirmModal'
import VRichText from '@/components/base/VRichText'
import VAlert from '@/components/base/VAlert'
// import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseJSON, format } from 'date-fns'

export default {
  components: { 
    VTextField, VFileUpload, VDropZone, VConfirmModal, VRichText, VAlert
  },
  data() {
    return {
      editingId: null,
      template: {
        elevation: '',
        image: {
          file: '',
          name: '',
          url: ''
        }
      },
      files: [
        {
          elevation: '',
          image: {
            file: '',
            name: '',
            url: ''
          }
        }
      ],
      confirmDialog: {
        state: false,
        title: 'Confirm mark as complete?',
        note: '',
        message: '',
        type: 'info'
      },
      confirmedCb: null,
      comments: '',
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => date ? format(date, "yyyy-MM-dd HH:mm:ss") : date
    }
  },
  computed: {
    ...mapState("drawingHighlight", [
      "drawingHighlight", "loading", "firstLoad"
    ]),
    ...mapState("elevations", ["elevations"]),
    ...mapState("auth", ["user"]),
    can({ user }) {
      return {
        markAsCompleted: ["ceo", "sales-estimator", "estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin',
        markAsApproved: ["ceo", "sales", "sales-estimator", "estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin'
      }
    },
    editable({ can }) {
      return this.drawingHighlight.project_rfq.is_accepted && 
        !this.drawingHighlight.is_completed && can.markAsCompleted
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("drawingHighlight", [
      "getDrawingHighlight", "updateDrawingHighlight",
      "addFile", "updateFile", "deleteFile", "addSvg"
    ]),
    ...mapActions("elevations", ["getElevations"]),
    ...mapMutations("drawingHighlight", [
      "setDrawingHighlight", "setLoading", "setFirstLoad"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.drawingHighlight) {
        this.setDrawingHighlight(this.$route.params.drawingHighlight);
        if (this.drawingHighlight.files.length > 0) {
          this.files = [...this.drawingHighlight.files];
          this.files.sort((a,b) => +a.id - +b.id);
        }
        if (this.elevations.length == 0) {
          this.getElevations();
        }
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        if (this.elevations.length == 0) {
          this.getElevations();
        }
        this.getDrawingHighlight(
          this.$route.params.drawingHighlightId
        ).then(() => {
          if (this.drawingHighlight.files.length > 0) {
            this.files = [...this.drawingHighlight.files];
            this.files.sort((a,b) => +a.id - +b.id);
          }
        });
      }
    },
    addHighlight() {
      this.files.push({...this.template});
    },
    confirmMarkComplete() {
      this.confirmedCb = () => {
        this.updateDrawingHighlight({
          drawingHighlightId: this.drawingHighlight.id,
          data: { 
            is_completed: true,
            comments: this.comments 
          }
        }).then(() => {
          this.comments = '';
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm mark as complete'
      this.confirmDialog.note = 'Note that once a drawing highlight \
        is marked as completed, you will not be able to make further \
        changes unless a revision is requested.';
      this.confirmDialog.state = true;
    },
    confirmMarkApproved() {
      this.confirmedCb = () => {
        this.updateDrawingHighlight({
          drawingHighlightId: this.drawingHighlight.id,
          data: { 
            is_approved: true,
            comments: this.comments 
          }
        }).then(() => {
          this.comments = '';
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Confirm mark as approved'
      this.confirmDialog.note = 'Note that once a drawing highlight \
          is marked as approved, you will not be able to ask for \
          further revisions.';
      this.confirmDialog.state = true;
    },
    confirmRevision() {
      this.confirmedCb = () => {
        this.updateDrawingHighlight({
          drawingHighlightId: this.drawingHighlight.id,
          data: { 
            is_in_revision: true,
            comments: this.comments 
          }
        }).then(() => {
          this.comments = '';
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Leave notes for revision'
      this.confirmDialog.note = 'Please let the estimator \
        know what needs to be revised in the comment box.';
      this.confirmDialog.state = true;
    },
    addComment() {
      this.confirmedCb = () => {
        this.updateDrawingHighlight({
          drawingHighlightId: this.drawingHighlight.id,
          data: {
            comments: this.comments 
          }
        }).then(() => {
          this.comments = '';
        })
        this.confirmDialog.state = false;
      }
      this.confirmDialog.title = 'Add a comment'
      this.confirmDialog.note = '';
      this.confirmDialog.state = true;
    },
    addMasterHighlightSvg(file) {
      let formData = new FormData();
      formData.append('svg', file);
      this.addSvg({
        drawingHighlightId: this.drawingHighlight.id,
        data: formData,
        params: { hypertext: file.name }
      })
    },
    saveHighlightFile(file, i) {
      let formData = new FormData();
      formData.append('image', file.image.file, file.image.file.name);

      this.addFile({
        data: formData,
        params: { 
          drawing_highlight_id: this.drawingHighlight.id,
          elevation_id: file.elevation_id,
          elevation: file.elevation,
          hypertext: file.image.file.name
        }
      }).then(resp => {
        this.files.splice(i, 1, resp.data);
      });
    },
    deleteHighlightFile(fileId) {
      this.deleteFile(fileId).then(() => {
        this.files.forEach((file, i) => {
          if (file.id == fileId) {
            this.files.splice(i, 1);
          }
        })
      })
    },
    updateElevation(fileId, field, value) {
      let payload = {}
      payload[field] = value;

      this.updateFile({
        fileId: fileId,
        data: payload
      });
    },
    updateFileImage(file) {
      let formData = new FormData();
      formData.append('image', file.image.file, file.image.file.name);

      this.updateFile({
        fileId: file.id,
        data: formData
      }).then(resp => {
        this.replace(resp.data);
      })
    },
    replace(obj) {
      this.files.forEach((file, i) => {
        if (file.id == obj.id) {
          this.files.splice(i, 1, obj);
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.highlight {
  &__container {
    display: flex;
    flex-direction: row;
    min-height: calc(100vh - 52px);
  }
  &__info {
    padding: 1.5em;
    border-right: 1px solid gainsboro;
    width: 20%;
  }
  &__details {
    padding: 1.5em;
    width: 80%;
  }
}
</style>